import { useState, useEffect } from "react";
import { fetchAuditLogsApi } from "../Lolbin/Api/lolbinapi";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  Settings,
  ArrowLeft,
  CheckCircle,
  X,
  AlertTriangle,
  Key,
  BarChart3,
  Activity,
  Shield,
  Bell,
  Server,
  Globe,
  Monitor,
  AlertCircle,
  Zap,
  TrendingUp
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import AuditLogsDetails from "../AuditLogs/AuditLogsDetails";
import AlertDetails from "../Alerts/AlertsDetails";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";

interface IPAddressUsage {
  ipAddress: string;
  accessCount: number;
  lastSeen: string;
}


interface SPNOperation {
  spn: string;
  accessCount: number;
  lastUsed: string;
}


const isInternalIP = (ip: string): boolean => {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4) return false;
  if (parts[0] === 10) return true;
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
  if (parts[0] === 192 && parts[1] === 168) return true;
  if (parts[0] === 127) return true;
  return false;
};


export default function ADServiceAccountAnalytics() {
  const [lastActivity, setLastActivity] = useState<string>("-");
  const params = useParams();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("alerts");
  const navigate = useNavigate();
  const computerEmail = params.name || '';
  console.log('Service Account Name:', computerEmail);


  // State for dynamic analytics
  const [activityTimeline, setActivityTimeline] = useState<{ time: string; events: number }[]>([]);
  const [topIPAddresses, setTopIPAddresses] = useState<IPAddressUsage[]>([]);
  const [spnOperations, setSpnOperations] = useState<SPNOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const { email, role } = useUserStore((state) => state.user);



  const fetchData = async () => {
    let latestTimestamp = "";
    // Helper to normalize ::ffff:IPv4 to IPv4
    const normalizeIP = (ip: string) => {
      const match = ip.match(/^::ffff:(\d+\.\d+\.\d+\.\d+)$/);
      return match ? match[1] : ip;
    };
    setLoading(true);
    try {
      const bucket = 'ondevice-auditlog';
      const filterPagination = 'lm';
      const computerName = '';

      const response = await fetchAuditLogsApi(
        "ondevice-auditlog",
        tenantName,
        filterPagination,
        computerName,
        "",
        computerEmail,
        "",
        email,
        true,
        "",
        "",
        1,
        5000,

      );
      if (response) {

        let logs = response || [];
        const timelineMap = new Map<string, number>();
        const ipMap = new Map<string, { count: number; lastSeen: string }>();
        const spnMap = new Map<string, { count: number; lastUsed: string }>();
        let foundRelevantField = false;

        logs.forEach((log: any) => {
          // Track latest activity timestamp
          if (log._time && (!latestTimestamp || log._time > latestTimestamp)) {
            latestTimestamp = log._time;
          }
          // After processing logs, set last activity
          if (latestTimestamp) {
            const d = new Date(latestTimestamp);
            setLastActivity(isNaN(d.getTime()) ? latestTimestamp : d.toLocaleString());
          } else {
            setLastActivity("-");
          }
          let event: Record<string, any> = {};
          try {
            event = log?.eventJson ? JSON.parse(log.eventJson) as Record<string, any> : {};
          } catch (e) { event = {}; }

          if (log._time) {
            const date = new Date(log._time);
            const day = date.toISOString().split('T')[0];
            timelineMap.set(day, (timelineMap.get(day) || 0) + 1);
          }

          let srcIP = '';
          if (event.IpAddress && typeof event.IpAddress === 'string') {
            srcIP = event.IpAddress;
          } else if (event.SourceIp && typeof event.SourceIp === 'string') {
            srcIP = event.SourceIp;
          }
          srcIP = srcIP ? normalizeIP(srcIP) : srcIP;

          foundRelevantField = true;
          const prev = ipMap.get(srcIP);
          if (!prev || (log._time && log._time > prev.lastSeen)) {
            ipMap.set(srcIP, { count: (prev ? prev.count : 0) + 1, lastSeen: log._time || '' });
          } else {
            ipMap.set(srcIP, { count: (prev ? prev.count : 0) + 1, lastSeen: prev.lastSeen });
          }
          let svc = '';
          if (typeof event['ServiceName'] === 'string') {
            svc = event['ServiceName'];
          } else if (typeof event['Service'] === 'string') {
            svc = event['Service'];
          } else if (typeof event['ServicePrincipalName'] === 'string') {
            svc = event['ServicePrincipalName'];
          }
          if (svc) {
            foundRelevantField = true;
            const prev = spnMap.get(svc);
            if (!prev || (log._time && log._time > prev.lastUsed)) {
              spnMap.set(svc, { count: (prev ? prev.count : 0) + 1, lastUsed: log._time || '' });
            } else {
              spnMap.set(svc, { count: (prev ? prev.count : 0) + 1, lastUsed: prev.lastUsed });
            }
          }
        });

        // Prepare timeline (last 7 days)
        const now = new Date();
        const timelineArr: { time: string; events: number }[] = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - i);
          const key = d.toISOString().split('T')[0];
          timelineArr.push({ time: `${i === 0 ? 'Today' : `${i}d ago`}`, events: timelineMap.get(key) || 0 });
        }
        setActivityTimeline(timelineArr);

        // Prepare top IPs, filter out empty
        setTopIPAddresses(
          Array.from(ipMap.entries())
            .filter(([ip]) => ip && ip.trim() !== "")
            .map(([ip, v]) => ({ ipAddress: ip, accessCount: v.count, lastSeen: v.lastSeen }))
            .sort((a, b) => b.accessCount - a.accessCount)
            .slice(0, 10)
        );

        // Prepare top SPNs
        const spnArr = Array.from(spnMap.entries()).map(([spn, v]) => ({ spn, accessCount: v.count, lastUsed: v.lastUsed }));
        spnArr.sort((a, b) => b.accessCount - a.accessCount);
        setSpnOperations(spnArr.slice(0, 10));

      }


      // If no relevant fields found, optionally show a message (handled in render)
    } catch (e) {
      setActivityTimeline([]);
      setTopIPAddresses([]);
      setSpnOperations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computerEmail, activeTab]);


  // Remove or replace entityData usage since it's not defined anymore
  // const unresolvedAlerts = entityData.alerts.filter(a => !a.resolved).length;

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4" onClick={() => navigate(-1)}>
        <Button variant="ghost" size="sm" className="gap-2" data-testid="button-back">
          <ArrowLeft className="w-4 h-4" />
          Back to AD Management
        </Button>
      </div>

      {/* Entity Header Card - Compact with Stats */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center border bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50 border-blue-200 dark:border-blue-700">
                <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-xl font-bold tracking-tight" data-testid="text-entity-name">
                    {computerEmail}
                  </h1>
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                </div>
                <div className="flex items-center gap-6 text-xs text-muted-foreground">
                  {/*<span>Managed by <span className="font-medium text-foreground">{entityData.managedBy}</span></span>*/}
                  {/* No lastLogin available from API, so omit or use a placeholder */}
                  <span>Last activity <span className="font-medium text-foreground">{lastActivity}</span></span>
                </div>
              </div>
            </div>
            {/* Quick Stats on right */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-600" />
                <div>
                  <div className="text-lg font-bold">{topIPAddresses.length}</div>
                  <div className="text-[10px] text-muted-foreground">IP Addresses</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-lg font-bold">{spnOperations.length}</div>
                  <div className="text-[10px] text-muted-foreground">SPNs</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card className="flex-1">
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">

              <TabsTrigger value="alerts" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Bell className="w-4 h-4" />
                Alerts
              </TabsTrigger>
              <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Activity className="w-4 h-4" />
                Activity
              </TabsTrigger>
              {/**
               * <TabsTrigger value="suggestions" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Zap className="w-4 h-4" />
                Suggestions
              </TabsTrigger>
               */}

            </TabsList>


            {/* Overview Tab - Dynamic Data */}
            <TabsContent value="overview" className="space-y-6 mt-6">
              {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <Skeleton className="h-6 w-40 mb-2" />
                      <Skeleton className="h-4 w-32" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-[280px] w-full" />
                    </CardContent>
                  </Card>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <Skeleton className="h-6 w-40 mb-2" />
                        <Skeleton className="h-4 w-32" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-16 w-full mb-2" />
                        <Skeleton className="h-16 w-full" />
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <Skeleton className="h-6 w-40 mb-2" />
                        <Skeleton className="h-4 w-32" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-16 w-full mb-2" />
                        <Skeleton className="h-16 w-full" />
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                    {/* Activity Timeline Graph */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base font-semibold">
                          <TrendingUp className="w-4 h-4 text-blue-600" />
                          Activity Timeline
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">Event frequency over last 7 days</p>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={activityTimeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                              <defs>
                                <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05} />
                                </linearGradient>
                              </defs>
                              <XAxis
                                dataKey="time"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10, fill: '#9CA3AF' }}
                                interval="preserveStartEnd"
                              />
                              <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10, fill: '#9CA3AF' }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--card))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px',
                                  fontSize: '12px'
                                }}
                                labelStyle={{ fontWeight: 600 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="events"
                                stroke="#3B82F6"
                                strokeWidth={2}
                                fill="url(#colorEvents)"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top SPNs - Progress Bar Style */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base font-semibold">
                          <Key className="w-4 h-4 text-purple-600" />
                          Top SPNs
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">Most used Service Principal Names</p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2.5">
                          {(() => {
                            const total = spnOperations.reduce((s, spn) => s + spn.accessCount, 0);
                            const maxCount = spnOperations[0]?.accessCount || 1;
                            return spnOperations.map((spn, idx) => {
                              const pct = (spn.accessCount / total) * 100;
                              const barWidth = (spn.accessCount / maxCount) * 100;
                              return (
                                <div key={idx} className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-mono">{spn.spn}</span>
                                    <span className="text-[10px] text-muted-foreground">{pct.toFixed(1)}%</span>
                                  </div>
                                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-500"
                                      style={{ width: `${barWidth}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                    {/* Top IP Addresses - Progress Bar Style */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base font-semibold">
                          <Globe className="w-4 h-4 text-blue-600" />
                          Top IP Addresses
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">Most frequent source IPs</p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2.5">
                          {(() => {
                            console.log(topIPAddresses)
                            const total = topIPAddresses.reduce((s, ip) => s + ip.accessCount, 0);
                            const maxCount = topIPAddresses[0]?.accessCount || 1;
                            return topIPAddresses.map((ip, idx) => {
                              const pct = (ip.accessCount / total) * 100;
                              const barWidth = (ip.accessCount / maxCount) * 100;
                              // Show badge only if value is a real IP address
                              //const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(ip.ipAddress);
                              const isIP = true;
                              return (
                                <div key={idx} className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-mono">{ip.ipAddress}</span>
                                      {isIP && !isInternalIP(ip.ipAddress) && (
                                        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800">External</Badge>
                                      )}
                                    </div>
                                    <span className="text-[10px] text-muted-foreground">{pct.toFixed(1)}%</span>
                                  </div>
                                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                                      style={{ width: `${barWidth}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4 mt-6">
              <AuditLogsDetails fromdetails={true} computerName="" computeremail={computerEmail} />
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts" className="space-y-4 mt-6">
              <Card>
                <CardContent>
                  <AlertDetails fromdetails={true} computerName="" computeremail={computerEmail} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Suggestions Tab (disabled, no entityData) */}
            {/*
            <TabsContent value="suggestions" className="space-y-4 mt-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Zap className="w-5 h-5 text-amber-500" />
                    AI-Driven Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {entityData.suggestions.map((suggestion) => (
                      <Card key={suggestion.id} className="border-border/40">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs capitalize">
                                  {suggestion.type.replace('_', ' ')}
                                </Badge>
                                <Badge className={getRiskColor(suggestion.impact === 'high' ? 'high' : suggestion.impact === 'medium' ? 'medium' : 'low')}>
                                  {suggestion.impact} impact
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {suggestion.confidence}% confidence
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">{suggestion.reason}</p>
                              <div className="space-y-1">
                                {suggestion.evidence.map((evidence, idx) => (
                                  <div key={idx} className="text-xs text-muted-foreground flex items-center gap-1">
                                    <span className="text-muted-foreground">•</span> {evidence}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApplySuggestion(suggestion.id, suggestion.type)}
                              className="gap-2"
                              data-testid={`button-apply-${suggestion.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Apply
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            */}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
